import {AfterViewInit, Component, Input, OnInit, signal, ViewChild, WritableSignal} from '@angular/core';
import {IControlTiempo, IRangoTiempo} from "../../../shared/interfaces/database/configuracion";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {
  ICustomTable, LoadingPanelService, LocalService, ISettings,
  CustomTableComponent, CustomTitle, ButtonColumnType,
  FormGroupErrorMessageComponent, AccessRole, DataColumnType
} from "lib-base";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import { MessageService} from "primeng/api";
import {DialogModule} from "primeng/dialog";
import {firstValueFrom, forkJoin} from "rxjs";
import {Event} from "@angular/router";
import {RangoTiempoService} from "../../../services/database/rango-tiempo.service";
import {InputTextModule} from "primeng/inputtext";
import {InputNumberModule} from "primeng/inputnumber";
import {ButtonDirective} from "primeng/button";
import {Colors} from "../../../shared/clases/Colors";
import {DropdownModule} from "primeng/dropdown";
import {UpperCasePipe} from "@angular/common";


@Component({
  selector: 'app-rango-tiempo',
  standalone: true,
  imports: [
    DialogModule,
    CustomTableComponent, CustomTitle,
    FormGroupErrorMessageComponent, TranslatePipe, ReactiveFormsModule, InputTextModule, InputNumberModule, ButtonDirective, DropdownModule, UpperCasePipe,
  ],
  templateUrl: './rango-tiempo.component.html',
  styleUrl: './rango-tiempo.component.scss'
})
export class RangoTiempoComponent implements OnInit, AfterViewInit {
  @Input({required: true}) value: Required<Omit<IControlTiempo, 'rangos'>> | undefined;
  colors:any[] = [];
  form!: FormGroup;
  openDialog: boolean = false;
  configTable!: ICustomTable;
  translations: any;
  role = AccessRole;
  private id: number | undefined;
  title: WritableSignal<string> = signal<string>('');
  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;


  constructor(private _t: TranslateService, private readonly _lps: LoadingPanelService,
              private readonly rangoTiempService: RangoTiempoService,
              protected readonly ls: LocalService, private messageService: MessageService) {
    this.initForm();
    this.getColors();
  }

  ngOnInit(): void {
    this.intTranslations()
      .then(() => this.initTable())
  }

  ngAfterViewInit(): void {
    this.getAllRangosByControl()
  }

  private initForm() {
    this.form = new FormGroup({
      diaInicio: new FormControl(null, Validators.required),
      diaFinal: new FormControl(null),
      indicador: new FormControl(null, Validators.required),
      color: new FormControl(null),
    });
  }

  private async intTranslations(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      diaInicial: this._t.get('components.app-rango-tiempo.dia-inicial'),
      diaFinal: this._t.get('components.app-rango-tiempo.dia-final'),
      indicador: this._t.get('components.app-rango-tiempo.indicador'),
      success: this._t.get('common.success'),
      summaryWarn: this._t.get('form.msg.warn-summary'),
      rangoCreado: this._t.get('components.app-rango-tiempo.rango-creado'),
      rangoActualizado: this._t.get('components.app-rango-tiempo.rango-actualizado'),
      titleActualizar: this._t.get('components.app-rango-tiempo.title-actualizar'),
      titleCrear: this._t.get('components.app-rango-tiempo.title-crear'),
      requiereRolSeguridad: this._t.get('msg.requiere-rol-seguridad'),
    }))
  }

  private initTable(): void {
    this.configTable = {
      columns: [
        {
          field: 'diaInicial',
          header: this.translations.diaInicial,
          class: 'font-13',
          width: '12em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          columnFilter: true,
          dataType: DataColumnType.NUMBER
        },
        {
          field: 'diaFinal',
          header: this.translations.diaFinal,
          class: 'font-13',
          width: '12em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          columnFilter: true,
          dataType: DataColumnType.NUMBER
        },
        {
          field: 'indicador',
          header: this.translations.indicador,
          class: 'font-13',
          width: '12em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: false,
          columnFilter: false,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'edit',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pr-1',
          buttonType: ButtonColumnType.EDIT,
          disabled: !this.ls.hasRole(this.role.ADMIN_SEC),
          tooltip: {
            message: this.translations.requiereRolSeguridad
          },
          onClick: (event: Event, data: IRangoTiempo) =>
            this.editRegistry(data),
          headerBtn: {
            icon: 'pi pi-plus',
            class: 'pt-2 pb-2 pl-0 pr-0',
            onClick: () => this.newRegister(),
          }
        }
      ],
      settings: {
        table: {
          rows: 8
        }
      } as ISettings
    } as ICustomTable;
  }

  getAllRangosByControl() {
    if (!this.value) return;
    this.rangoTiempService.getAllByControlTiempoId(this.value.id, this.value.version).subscribe({
      next: (rangos: IRangoTiempo[]) => {
        if (this.customTableComponent)
          this.customTableComponent.data = rangos;
      }
    })
  }

  hideModal(): void {
    this.form.reset();
    this.id = undefined;
  }

  editRegistry(event: IRangoTiempo) {
    this.title.set(this.translations.titleActualizar);
    this.id = event.id;
    this.form.setValue({
      indicador: event.indicador,
      diaFinal: event.diaFinal,
      diaInicio: event.diaInicial,
      color: event?.color ?? null
    });
    this.openDialog = true;
  }

  newRegister(): void {
    this.title.set(this.translations.titleCrear);
    this.openDialog = true;
  }

  private build(): IRangoTiempo {
    return {
      indicador: this.form.get('indicador')?.value,
      diaInicial: this.form.get('diaInicio')?.value,
      diaFinal: this.form.get('diaFinal')?.value,
      controlTiempoId: this.value?.id,
      controlTiempoVersion: this.value?.version,
      color:this.form.get('color')?.value,
    };

  }


  onSubmit() {
    this._lps.show();
    const body = this.build();
    if (!this.id) {
      this.rangoTiempService.crear(body).subscribe({
        next: () => {
          this.updateContent(this.translations.rangoCreado);
        }
      })

    }else{
      this.rangoTiempService.modificar(this.id,body).subscribe({
        next: () => {
          this.updateContent(this.translations.rangoActualizado);
        }
      })
    }
  }

  getColors(){
    this.colors =  Colors;
  }



  private updateContent(message:string) {
    this.getAllRangosByControl();
    this.messageService.add({
      severity: 'success',
      summary: this.translations.success,
      detail: message
    });
    this._lps.hide();
    this.openDialog = false;

  }
}
